<template>
    <!-- Desktop variant: stats on image gradient overlay -->
    <div
        v-if="variant === 'desktop'"
        class="hidden md:flex flex-wrap justify-between pt-4 text-white z-10 transition-all w-full"
        :class="containerClass"
    >
        <!-- Redemptions -->
        <div
            class="flex items-center pt-8 px-4 pb-4 sm:px-2 sm:py-2 rounded-lg w-full sm:w-1/2 md:w-1/4 min-w-[180px]"
        >
            <div
                class="bg-gray-600/75 p-4 rounded-full flex items-center justify-center w-12 h-12"
            >
                <Icon icon="track" class="text-white" height="24" width="24" />
            </div>
            <div class="ml-4">
                <p class="text-xl font-bold sm:text-base md:text-lg">
                    {{ trip.redemption_count }}
                </p>
                <p class="text-md sm:text-sm">Redemptions</p>
            </div>
        </div>

        <!-- Total value -->
        <div
            class="flex items-center pt-8 px-4 pb-4 sm:px-2 sm:py-2 rounded-lg w-full sm:w-1/2 md:w-1/4 min-w-[180px]"
        >
            <div
                class="bg-gray-600/75 p-4 rounded-full flex items-center justify-center w-12 h-12"
            >
                <Icon
                    icon="dollar-bill"
                    class="text-white"
                    height="24"
                    width="24"
                />
            </div>
            <div class="ml-4">
                <div class="flex items-center">
                    <p
                        class="text-xl font-bold sm:text-base md:text-lg text-left"
                    >
                        {{ formatCurrency(totalValue) }}
                    </p>
                    <Icon
                        icon="chevron-down"
                        :width="16"
                        :height="16"
                        class="text-white cursor-pointer icon-down ml-2"
                        @click="togglePopover('popover1')"
                    />
                </div>
                <div
                    v-if="isPopoverOpen.popover1"
                    v-click-outside="() => closePopover('popover1')"
                    class="absolute transform -translate-x-[60%] z-10 mt-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg w-60 p-4 flex-col items-start gap-3"
                >
                    <template v-if="totalValue === 0">
                        <p class="text-slate-500 dark:text-slate-400 text-[14px] font-medium">
                            Details will display once you begin to add
                            redemptions to this trip.
                        </p>
                    </template>
                    <template v-else>
                        <div class="flex justify-between">
                            <h3
                                class="text-slate-500 dark:text-slate-400 text-[14px] font-medium"
                            >
                                Cash cost
                            </h3>
                            <span class="text-[14px] text-slate-800 dark:text-slate-100">
                                {{ formatCurrency(trip.cash_value) }}
                            </span>
                        </div>
                        <div class="mt-4 flex justify-between">
                            <h3
                                class="text-slate-500 dark:text-slate-400 text-[14px] font-medium"
                            >
                                Taxes & fees
                            </h3>
                            <span
                                class="text-[14px] text-right text-slate-800 dark:text-slate-100"
                            >
                                {{ formatCurrency(trip.taxes_value) }}
                            </span>
                        </div>
                        <div class="mt-2 w-52 h-0.5 bg-[#E2E8F0]"></div>
                        <div class="mt-4 flex justify-between">
                            <h3
                                class="text-slate-500 dark:text-slate-400 text-[14px] font-medium"
                            >
                                Total Value
                            </h3>
                            <span
                                class="text-[14px] text-right text-slate-800 dark:text-slate-100"
                            >
                                {{ formatCurrency(totalValue) }}
                            </span>
                        </div>
                    </template>
                </div>
                <p class="text-md sm:text-sm">Total value</p>
            </div>
        </div>

        <!-- Cash spent -->
        <div
            class="flex items-center pt-8 px-4 pb-4 sm:px-2 sm:py-2 rounded-lg w-full sm:w-1/2 md:w-1/4 min-w-[180px]"
        >
            <div
                class="bg-gray-600/75 p-4 rounded-full flex items-center justify-center w-12 h-12"
            >
                <Icon
                    icon="dollar-coin"
                    class="text-white"
                    height="24"
                    width="24"
                />
            </div>
            <div class="ml-4">
                <div class="flex items-center">
                    <p
                        class="text-xl font-bold sm:text-base md:text-lg text-left"
                    >
                        {{ formatCurrency(cashSpentTotal) }}
                    </p>
                    <Icon
                        icon="chevron-down"
                        :width="16"
                        :height="16"
                        class="text-white cursor-pointer icon-down ml-2"
                        @click="togglePopover('popover2')"
                    />
                </div>
                <div
                    v-if="isPopoverOpen.popover2"
                    v-click-outside="() => closePopover('popover2')"
                    class="absolute transform -translate-x-[60%] z-10 mt-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg w-60 p-4 flex-col items-start gap-3"
                >
                    <template v-if="trip.cash_spend_value === 0">
                        <p class="text-slate-500 dark:text-slate-400 text-[14px] font-medium">
                            Details will display once you begin to add
                            redemptions to this trip.
                        </p>
                    </template>
                    <template v-else>
                        <div class="flex justify-between">
                            <h3
                                class="text-slate-500 dark:text-slate-400 text-[14px] font-medium"
                            >
                                Cash cost
                            </h3>
                            <span class="text-[14px] text-slate-800 dark:text-slate-100">
                                {{ formatCurrency(trip.cash_spend_value) }}
                            </span>
                        </div>
                        <div class="mt-4 flex justify-between">
                            <h3
                                class="text-slate-500 dark:text-slate-400 text-[14px] font-medium"
                            >
                                Taxes & fees
                            </h3>
                            <span
                                class="text-[14px] text-right text-slate-800 dark:text-slate-100"
                            >
                                {{
                                    formatCurrency(
                                        trip.cash_spend_taxes_value
                                    )
                                }}
                            </span>
                        </div>
                        <div class="mt-2 w-52 h-0.5 bg-[#E2E8F0]"></div>
                        <div class="mt-4 flex justify-between">
                            <h3
                                class="text-slate-500 dark:text-slate-400 text-[14px] font-medium"
                            >
                                Total Value
                            </h3>
                            <span
                                class="text-[14px] text-right text-slate-800 dark:text-slate-100"
                            >
                                {{ formatCurrency(cashSpentTotal) }}
                            </span>
                        </div>
                    </template>
                </div>
                <p class="text-md sm:text-sm">Cash spent</p>
            </div>
        </div>

        <!-- Points spent -->
        <div
            class="flex items-center pt-8 px-4 pb-4 sm:px-2 sm:py-2 rounded-lg w-full sm:w-1/2 md:w-1/4 min-w-[180px]"
        >
            <div
                class="bg-gray-600/75 p-4 rounded-full flex items-center justify-center w-12 h-12"
            >
                <Icon
                    icon="points"
                    class="text-white"
                    height="24"
                    width="24"
                />
            </div>
            <div class="ml-4">
                <div class="flex items-center">
                    <p
                        class="text-xl font-bold sm:text-base md:text-lg text-left"
                    >
                        {{ formatNumber(trip.payment_points) }}
                    </p>
                    <Icon
                        icon="chevron-down"
                        :width="16"
                        :height="16"
                        class="text-white cursor-pointer icon-down ml-2"
                        @click="togglePopover('popover3')"
                    />
                </div>
                <PointsPopover
                    :is-open="isPopoverOpen.popover3"
                    :trip="trip"
                    :computed-points-programs="computedPointsPrograms"
                    :cdn-url="cdnUrl"
                    :format-number="formatNumber"
                    @close="closePopover('popover3')"
                />
                <p class="text-md sm:text-sm">Points spent</p>
            </div>
        </div>
    </div>

    <!-- Mobile variant: grid stats below header -->
    <div
        v-else
        class="grid grid-cols-2 gap-2 text-black rounded-lg z-10 md:hidden"
    >
        <!-- Redemptions -->
        <div class="flex items-center pl-0 p-2 gap-2">
            <div class="flex items-center justify-start h-10">
                <Icon
                    icon="track"
                    class="text-gray-500 dark:text-gray-400"
                    height="20"
                    width="20"
                    containerClass="pl-0"
                />
            </div>
            <div class="ml-2">
                <p class="text-lg font-medium">
                    {{ trip.redemption_count }}
                </p>
                <p class="text-sm text-gray-500 dark:text-gray-400">Redemptions</p>
            </div>
        </div>

        <!-- Total value -->
        <div class="flex items-center pl-0 p-2 gap-2">
            <div class="flex items-center justify-start h-10">
                <Icon
                    icon="dollar-bill"
                    class="text-gray-500 dark:text-gray-400"
                    height="20"
                    width="20"
                />
            </div>
            <div class="ml-2">
                <div class="flex items-center">
                    <p class="text-lg font-medium">
                        {{ formatCurrency(totalValue) }}
                    </p>
                    <Icon
                        icon="chevron-down"
                        :width="14"
                        :height="14"
                        class="text-black cursor-pointer icon-down ml-1"
                        @click="togglePopover('popover1')"
                    />
                </div>
                <div
                    v-if="isPopoverOpen.popover1"
                    v-click-outside="() => closePopover('popover1')"
                    class="absolute transform -translate-x-[60%] z-10 mt-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg w-60 p-4 flex-col items-start gap-3"
                >
                    <template v-if="totalValue === 0">
                        <p class="text-slate-500 dark:text-slate-400 text-[12px] font-medium">
                            Details will display once you begin to add
                            redemptions to this trip.
                        </p>
                    </template>
                    <template v-else>
                        <div class="flex justify-between">
                            <h3
                                class="text-slate-500 dark:text-slate-400 text-[12px] font-medium"
                            >
                                Cash cost
                            </h3>
                            <span class="text-[12px] text-slate-800 dark:text-slate-100">
                                {{ formatCurrency(trip.cash_value) }}
                            </span>
                        </div>
                        <div class="mt-4 flex justify-between">
                            <h3
                                class="text-slate-500 dark:text-slate-400 text-[12px] font-medium"
                            >
                                Taxes & fees
                            </h3>
                            <span
                                class="text-[12px] text-right text-slate-800 dark:text-slate-100"
                            >
                                {{ formatCurrency(trip.taxes_value) }}
                            </span>
                        </div>
                        <div class="mt-2 w-52 h-0.5 bg-[#E2E8F0]"></div>
                        <div class="mt-4 flex justify-between">
                            <h3
                                class="text-slate-500 dark:text-slate-400 text-[12px] font-medium"
                            >
                                Total Value
                            </h3>
                            <span
                                class="text-[12px] text-right text-slate-800 dark:text-slate-100"
                            >
                                {{ formatCurrency(totalValue) }}
                            </span>
                        </div>
                    </template>
                </div>
                <p class="text-sm text-gray-500 dark:text-gray-400">Total value</p>
            </div>
        </div>

        <!-- Cash spent -->
        <div class="flex items-center pl-0 p-2 gap-2">
            <div class="flex items-center justify-start h-10">
                <Icon
                    icon="dollar-coin"
                    class="text-gray-500 dark:text-gray-400"
                    height="20"
                    width="20"
                />
            </div>
            <div class="ml-2">
                <div class="flex items-center">
                    <p class="text-lg font-medium">
                        {{ formatCurrency(cashSpentTotal) }}
                    </p>
                    <Icon
                        icon="chevron-down"
                        :width="14"
                        :height="14"
                        class="text-black cursor-pointer icon-down ml-1"
                        @click="togglePopover('popover2')"
                    />
                </div>
                <div
                    v-if="isPopoverOpen.popover2"
                    v-click-outside="() => closePopover('popover2')"
                    class="absolute left-auto right-30 md:right-[5px] md:left-auto z-10 mt-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-lg w-40 md:w-60 p-2 md:p-4 flex-col items-start gap-2 md:gap-3"
                >
                    <template v-if="trip.cash_spend_value === 0">
                        <p class="text-slate-500 dark:text-slate-400 text-[12px] font-medium">
                            Details will display once you begin to add
                            redemptions to this trip.
                        </p>
                    </template>
                    <template v-else>
                        <div class="flex justify-between">
                            <h3
                                class="text-slate-500 dark:text-slate-400 text-[12px] font-medium"
                            >
                                Cash cost
                            </h3>
                            <span class="text-[12px] text-slate-800 dark:text-slate-100">
                                {{ formatCurrency(trip.cash_spend_value) }}
                            </span>
                        </div>
                        <div class="mt-4 flex justify-between">
                            <h3
                                class="text-slate-500 dark:text-slate-400 text-[12px] font-medium"
                            >
                                Taxes & fees
                            </h3>
                            <span
                                class="text-[12px] text-right text-slate-800 dark:text-slate-100"
                            >
                                {{
                                    formatCurrency(
                                        trip.cash_spend_taxes_value
                                    )
                                }}
                            </span>
                        </div>
                        <div class="mt-2 w-52 h-0.5 bg-[#E2E8F0]"></div>
                        <div class="mt-4 flex justify-between">
                            <h3
                                class="text-slate-500 dark:text-slate-400 text-[12px] font-medium"
                            >
                                Total Value
                            </h3>
                            <span
                                class="text-[12px] text-right text-slate-800 dark:text-slate-100"
                            >
                                {{ formatCurrency(cashSpentTotal) }}
                            </span>
                        </div>
                    </template>
                </div>
                <p class="text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                    Cash spent
                </p>
            </div>
        </div>

        <!-- Points spent -->
        <div class="flex items-center pl-0 p-2 gap-2">
            <div class="flex items-center justify-start h-10">
                <Icon
                    icon="points"
                    class="text-gray-500 dark:text-gray-400"
                    height="20"
                    width="20"
                />
            </div>
            <div class="ml-2">
                <div class="flex items-center">
                    <p class="text-lg font-medium">
                        {{ formatNumber(trip.payment_points) }}
                    </p>
                    <Icon
                        icon="chevron-down"
                        :width="14"
                        :height="14"
                        class="text-black cursor-pointer icon-down ml-1"
                        @click="togglePopover('popover3')"
                    />
                </div>
                <PointsPopover
                    :is-open="isPopoverOpen.popover3"
                    :trip="trip"
                    :computed-points-programs="computedPointsPrograms"
                    :cdn-url="cdnUrl"
                    :format-number="formatNumber"
                    variant="mobile"
                    @close="closePopover('popover3')"
                />
                <p class="text-sm whitespace-nowrap text-gray-500 dark:text-gray-400">
                    Points spent
                </p>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, toRef } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import { useTripStats } from '@/composables/useTripStats';
import PointsPopover from './PointsPopover.vue';

const props = defineProps({
    trip: { type: Object, required: true },
    variant: { type: String, default: 'desktop' },
    containerClass: { type: String, default: '' },
});

const tripRef = toRef(props, 'trip');
const {
    formatCurrency,
    formatNumber,
    cdnUrl,
    totalValue,
    cashSpentTotal,
    computedPointsPrograms,
    isPopoverOpen,
    togglePopover,
    closePopover,
} = useTripStats(tripRef);
</script>
